/**
 * Accept or decline workspace invitation via ?token= (link or email).
 * Public route: prompts sign-in if needed; token kept in sessionStorage across login / OAuth.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import {
  acceptWorkspaceInvite,
  clearPendingInviteToken,
  getPendingInviteToken,
  rejectWorkspaceInvite,
  setPendingInviteToken,
  INVITE_BANNER_DISMISS_STORAGE_KEY,
} from '@/services/workspaceApi';
import { getRestApiBearerToken } from '@/services/apiToken';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export default function JoinWorkspacePage() {
  const {
    user,
    switchWorkspace,
    refreshBillingStatus,
    refreshWorkspaces,
    dismissPendingInviteByToken,
    workspaceRole,
  } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [working, setWorking] = useState(false);
  const inviteTokenParam = params.get('token') || '';

  useEffect(() => {
    const q = inviteTokenParam.trim();
    if (q) setPendingInviteToken(q);
  }, [inviteTokenParam]);

  useEffect(() => {
    const q = inviteTokenParam.trim();
    const stored = getPendingInviteToken().trim();
    const token = q || stored;
    if (!token) {
      setStatus('error');
      setMessage('Missing invite token. Open the full invitation link from your email or admin.');
      return;
    }
    setPendingInviteToken(token);
    if (!user) {
      setStatus('need_login');
      setMessage('Sign in or create an account, then accept this workspace invitation.');
      return;
    }
    setStatus('confirm');
    setMessage('');
  }, [user, inviteTokenParam]);

  const tokenForActions =
    inviteTokenParam.trim() || getPendingInviteToken().trim();

  const handleAccept = useCallback(async () => {
    const token = tokenForActions;
    if (!token || !user || working) return;
    setWorking(true);
    setMessage('');
    try {
      const bearer = getRestApiBearerToken();
      if (!bearer) {
        setStatus('error');
        setMessage('Session expired. Sign in again.');
        return;
      }
      const data = await acceptWorkspaceInvite(API_BASE_URL, bearer, token);
      const tid = (data.tenantId || '').trim();
      clearPendingInviteToken();
      try {
        sessionStorage.removeItem(INVITE_BANNER_DISMISS_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      dismissPendingInviteByToken(token);
      let postJoinRole = 'USER';
      if (tid) {
        const sw = await switchWorkspace(tid);
        postJoinRole = sw.workspaceRole || 'USER';
        await refreshBillingStatus();
        await refreshWorkspaces();
      }
      setStatus('ok');
      setMessage(data.workspaceName ? `Joined ${data.workspaceName}.` : 'Joined workspace.');
      setTimeout(
        () =>
          navigate(
            canAccessNetworkWorkbench(postJoinRole) ? '/network/new' : '/agentic',
            { replace: true },
          ),
        800,
      );
    } catch (e) {
      setStatus('error');
      // the link is used / expired / someone else's → drop it, so "You opened an invitation link" doesn't linger
      if ([404, 410].includes(e.status) || e.code === 'INVITE_EMAIL_MISMATCH') clearPendingInviteToken();
      if (e.code === 'INVITE_EMAIL_MISMATCH' && e.inviteEmail) {
        const as = e.signedInEmail || 'another account';
        setMessage(
          `This invite was sent to ${e.inviteEmail}, but you are signed in as ${as}. Sign out and sign in with the invited email address.`,
        );
      } else if (e.code === 'INVITE_EMAIL_NOT_VERIFIABLE' && e.inviteEmail) {
        setMessage(
          `This invite was sent to ${e.inviteEmail}. Your session does not include a verifiable email. Sign out and sign in with that address (Google or email/password).`,
        );
      } else {
        setMessage(e.message || 'Could not accept invitation.');
      }
    } finally {
      setWorking(false);
    }
  }, [
    tokenForActions,
    user,
    working,
    switchWorkspace,
    refreshBillingStatus,
    refreshWorkspaces,
    dismissPendingInviteByToken,
    navigate,
    workspaceRole,
  ]);

  const handleReject = async () => {
    const tok = tokenForActions;
    if (!tok || !user || working) return;
    setWorking(true);
    try {
      const bearer = getRestApiBearerToken();
      if (!bearer) {
        setStatus('error');
        setMessage('Session expired. Sign in again.');
        return;
      }
      await rejectWorkspaceInvite(API_BASE_URL, bearer, tok);
      clearPendingInviteToken();
      try {
        sessionStorage.removeItem(INVITE_BANNER_DISMISS_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      dismissPendingInviteByToken(tok);
      await refreshWorkspaces();
      setStatus('ok');
      setMessage('Invitation declined.');
      setTimeout(
        () =>
          navigate(
            canAccessNetworkWorkbench(workspaceRole) ? '/network/new' : '/agentic',
            { replace: true },
          ),
        800,
      );
    } catch (e) {
      setStatus('error');
      // the link is used / expired / someone else's → drop it, so "You opened an invitation link" doesn't linger
      if ([404, 410].includes(e.status) || e.code === 'INVITE_EMAIL_MISMATCH') clearPendingInviteToken();
      if (e.code === 'INVITE_EMAIL_MISMATCH' && e.inviteEmail) {
        const as = e.signedInEmail || 'another account';
        setMessage(
          `This invite was sent to ${e.inviteEmail}, but you are signed in as ${as}. Sign out and sign in with the invited email to decline.`,
        );
      } else if (e.code === 'INVITE_EMAIL_NOT_VERIFIABLE' && e.inviteEmail) {
        setMessage(
          `This invite was sent to ${e.inviteEmail}. Your session does not include a verifiable email. Sign out and sign in with that address.`,
        );
      } else {
        setMessage(e.message || 'Could not decline invitation.');
      }
    } finally {
      setWorking(false);
    }
  };

  if (status === 'need_login') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6">
        <p className="max-w-md text-center text-white">{message}</p>
        <Link
          to="/login"
          className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          Sign in
        </Link>
        <Link to="/" className="text-sm text-slate-400 hover:text-white">
          Home
        </Link>
      </div>
    );
  }

  if (status === 'confirm') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/20 ring-1 ring-blue-400/30">
            <svg
              className="h-8 w-8 text-blue-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white">Workspace invitation</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">
            Join this workspace on your account, or decline if you don’t want access. You can switch to
            another workspace anytime from the app menu.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            disabled={working}
            onClick={handleAccept}
            className="rounded-lg bg-blue-600 px-8 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-900/40 hover:bg-blue-700 disabled:opacity-50"
          >
            {working ? 'Working…' : 'Accept'}
          </button>
          <button
            type="button"
            disabled={working}
            onClick={handleReject}
            className="rounded-lg border border-slate-500 px-6 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
          >
            Decline
          </button>
        </div>
        <Link to="/network/new" className="text-sm text-slate-400 transition hover:text-white">
          Skip for now — go to app
        </Link>
      </div>
    );
  }

  if (status === 'idle') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-400 border-t-transparent" />
        <p className="text-sm text-slate-300">Preparing…</p>
      </div>
    );
  }

  if (status === 'ok') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6">
        <p className="text-center text-green-400">{message}</p>
        <p className="text-sm text-slate-500">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6">
      <p className="max-w-md text-center text-red-400">{message || 'Something went wrong.'}</p>
      <Link to="/network/new" className="text-sm text-blue-400 hover:underline">
        Back to app
      </Link>
    </div>
  );
}
